import React, { useState, useEffect, useReducer, useContext } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";

import SearchIcon from "@material-ui/icons/Search";
import WifiTethering from "@material-ui/icons/WifiTethering";
import {
  AddCircle,
  Build,
  ContentCopy,
  DevicesFold,
  MoreVert,
  Edit,
  Delete,
  PlayArrow,
  Pause,
  DragIndicator,
} from "@mui/icons-material";

import {
  Button,
  CircularProgress,
  Stack,
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Container,
  Fab,
  useScrollTrigger,
  Slide,
  AppBar,
  Toolbar,
  Divider,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Checkbox,
  Tooltip as MuiTooltip,
} from "@mui/material";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import FlowBuilderModal from "../../components/FlowBuilderModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import ForbiddenPage from "../../components/ForbiddenPage";
import NewTicketModal from "../../components/NewTicketModal";
import { SocketContext } from "../../context/Socket/SocketContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
  },
  mainContainer: {
    maxWidth: 1380,
    margin: '0 auto',
    padding: theme.spacing(3),
    background: theme.palette.type === 'dark'
      ? '#0B0F0C'
      : 'radial-gradient(circle at 8% 0%, rgba(0,219,117,.08), transparent 25%), #F5F8F6',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.type === 'dark' ? '#111713' : 'rgba(255,255,255,.78)',
    borderRadius: 20,
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,.08)' : '#DDE8E1'}`,
    boxShadow: '0 16px 42px rgba(10,35,22,.07)',
  },
  premiumHero: {
    position: 'relative', overflow: 'hidden', borderRadius: 16,
    padding: '16px 20px', marginBottom: 14, color: '#fff',
    background: 'linear-gradient(118deg, #080D0A 0%, #111A15 70%, #183626 100%)',
    border: '1px solid rgba(0,219,117,.18)',
    boxShadow: '0 10px 28px rgba(5,8,6,.14)',
  },
  premiumHeroGlow: {
    position: 'absolute', width: 180, height: 180, borderRadius: '50%',
    right: -50, top: -100, background: 'rgba(0,219,117,.11)', filter: 'blur(2px)',
  },
  heroEyebrow: { fontSize: 11, fontWeight: 900, letterSpacing: '1.3px', color: '#00DB75', textTransform: 'uppercase' },
  heroTitle: { fontSize: '1.28rem', fontWeight: 850, letterSpacing: '-.25px', marginTop: 3 },
  heroSubtitle: { fontSize: '.78rem', color: 'rgba(255,255,255,.66)', marginTop: 2, maxWidth: 650 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(105px, 1fr))', gap: 8, marginTop: 12, '@media (max-width: 700px)': { gridTemplateColumns: 'repeat(2, 1fr)' } },
  statItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 10, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.085)' },
  statValue: { display: 'block', fontSize: '1.02rem', fontWeight: 900, color: '#fff', lineHeight: 1 },
  statLabel: { display: 'block', marginTop: 0, fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,.55)', textTransform: 'uppercase', letterSpacing: '.55px' },
  
  header: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: 16,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: 12,
    },
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  searchContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: theme.shadows[1],
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
      borderRadius: 12,
    },
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.type === 'dark' ? '#151C17' : '#F4F8F5',
      borderRadius: 12,
      '& fieldset': {
        border: 'none',
      },
      '&:hover': {
        backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[700] : theme.palette.grey[100],
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 0 0 3px rgba(0,219,117,.14)',
      },
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
      fontSize: '0.95rem',
    },
  },
  flowCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 18,
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,.10)' : '#CADBD0'}`,
    borderLeft: '3px solid #00B965',
    boxShadow: '0 7px 22px rgba(10,35,22,.055), inset 0 1px 0 rgba(255,255,255,.8)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 15px 34px rgba(10,35,22,.12)',
      borderColor: '#65C997',
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1.5),
      borderRadius: 12,
    },
  },
  flowCardContent: {
    padding: theme.spacing(3),
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      '&:last-child': {
        paddingBottom: theme.spacing(2),
      },
    },
  },
  flowIcon: {
    width: 48,
    height: 48,
    background: 'linear-gradient(145deg, #07110C, #183426)',
    border: '1px solid rgba(0,219,117,.28)',
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: 40,
      height: 40,
      marginRight: theme.spacing(1.5),
    },
  },
  flowName: {
    fontSize: '1.125rem',
    fontWeight: 800,
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  flowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1.5),
    },
  },
  statusChip: {
    fontWeight: 800,
    borderRadius: 99,
    '&.active': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(0,219,117,.13)' : '#EAF9F1',
      color: theme.palette.type === 'dark' ? '#00DB75' : '#007A42',
      border: '1px solid rgba(0,168,90,.20)',
    },
    '&.inactive': {
      backgroundColor: theme.palette.type === 'dark' ? '#b71c1c' : '#ffebee',
      color: theme.palette.type === 'dark' ? '#f44336' : '#c62828',
      border: `1px solid ${theme.palette.type === 'dark' ? '#c62828' : '#ffcdd2'}`,
    },
  },
  actionButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    borderRadius: 10,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 36,
      width: 36,
      height: 36,
    },
  },
  menuButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    borderRadius: 10,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.text.secondary,
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4),
    },
  },
  emptyIcon: {
    fontSize: 64,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
  },
  emptyTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  emptyDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
  },
  addButton: {
    backgroundColor: '#00DB75',
    color: '#04130C',
    borderRadius: 12,
    padding: theme.spacing(1.5, 3),
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.95rem',
    boxShadow: theme.shadows[3],
    '&:hover': {
      backgroundColor: '#00C96C',
      boxShadow: theme.shadows[6],
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.2, 2.5),
    },
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: 64,
    height: 64,
    boxShadow: theme.shadows[6],
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: theme.shadows[12],
    },
    [theme.breakpoints.down('sm')]: {
      bottom: theme.spacing(2),
      right: theme.spacing(2),
      width: 56,
      height: 56,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  menu: {
    '& .MuiPaper-root': {
      borderRadius: 12,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[8],
      minWidth: 180,
      backgroundColor: theme.palette.background.paper,
    },
    '& .MuiMenuItem-root': {
      padding: theme.spacing(1.5, 2),
      fontSize: '0.9rem',
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
}));

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();
  
  return (
    <Slide appear={false} direction="up" in={!trigger}>
      {children}
    </Slide>
  );
}

function FlowCard({ flow, onEdit, onDuplicate, onDelete, onNavigate, onToggleActive, onExport, onTrigger, onConfigureConnections, allWhatsapps, classes }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action) => {
    handleMenuClose();
    // Usar setTimeout para garantir que o menu feche antes de executar a ação
    setTimeout(() => {
      action();
    }, 100);
  };

  const handleCardClick = (event) => {
    // Verifica se o clique foi em um botão de ação
    if (event.target.closest('button') || event.target.closest('[role="button"]')) {
      return;
    }
    onNavigate(flow.id);
  };

  const handleEditClick = (event) => {
    event.stopPropagation();
    onEdit();
  };

  const handleDuplicateClick = (event) => {
    event.stopPropagation();
    onDuplicate();
  };

  return (
    <Card className={classes.flowCard} onClick={handleCardClick}>
      <CardContent className={classes.flowCardContent}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <div className={classes.flowIcon}>
            <DevicesFold style={{ color: theme.palette.primary.contrastText, fontSize: isMobile ? 20 : 24 }} />
          </div>
          
          <Box flex={1}>
            <Typography className={classes.flowName}>
              {flow.name}
            </Typography>

            {((flow.triggers && flow.triggers.length > 0) || flow.isCatchAll) && (
              <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginTop: 6, marginBottom: 2 }}>
                {flow.isCatchAll && (
                  <Chip size="small" label="Fluxo Padrão"
                    style={{ background: "rgba(0,219,117,.12)", color: "#007A42", border: "1px solid rgba(0,168,90,.18)", fontWeight: 800, fontSize: "0.7rem" }}
                  />
                )}
                {(flow.triggers || []).map((t, i) => (
                  <Chip key={i} size="small" label={`"${t}"`}
                    style={{ background: "rgba(0,0,0,0.06)", fontSize: "0.68rem", maxWidth: 160 }}
                  />
                ))}
              </Box>
            )}

            {(() => {
              const sr = flow.scheduleRules;
              if (!sr || !sr.enabled || !sr.rules || sr.rules.length === 0) return null;
              const dayNames = ["Dom","Seg","Ter","Qua","Qui","Sex","Sab"];
              const lines = sr.rules.map((rule, i) => {
                const days = (rule.days || []).map(d => dayNames[d] || d).join(", ");
                return `${days}: ${rule.startTime || ""}–${rule.endTime || ""}`;
              });
              return (
                <Box display="flex" flexWrap="wrap" style={{ gap: 3, marginTop: 4, marginBottom: 2 }}>
                  {lines.map((line, i) => (
                    <Chip key={i} size="small" label={line}
                      style={{ background: "rgba(234,179,8,0.12)", color: "#b45309", fontSize: "0.65rem", border: "1px solid rgba(234,179,8,0.25)" }}
                    />
                  ))}
                </Box>
              );
            })()}

            <div className={classes.flowActions}>
              <Chip
                size="small"
                label={flow.active ? "Ativo" : "Inativo"}
                className={`${classes.statusChip} ${flow.active ? 'active' : 'inactive'}`}
                icon={flow.active ? <PlayArrow /> : <Pause />}
                onClick={() => onToggleActive && onToggleActive(flow.id, flow.active)}
                style={{ cursor: 'pointer' }}
              />
              
              <Box flex={1} />
              
              <IconButton
                className={classes.actionButton}
                onClick={handleEditClick}
                size="small"
              >
                <Edit fontSize="small" />
              </IconButton>
              
              <IconButton
                className={classes.actionButton}
                onClick={handleDuplicateClick}
                size="small"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
              
              <IconButton
                className={classes.menuButton}
                onClick={handleMenuOpen}
                size="small"
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </div>

            {/* Botao Conexoes — canto inferior */}
            <Box style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, marginTop: 6 }}>
              {(flow.whatsappIds && flow.whatsappIds.length > 0)
                ? (() => { const ns = allWhatsapps.filter(w => flow.whatsappIds.map(Number).includes(Number(w.id))).map(w => ({ id: w.id, name: w.name })); const items = ns.length > 0 ? ns : flow.whatsappIds.map(id => ({ id, name: `ID ${id}` })); return items.map((w, i) => (
                  <Chip
                    key={w.id || i}
                    size="small"
                    icon={i === 0 ? <WifiTethering style={{ fontSize: 14 }} /> : undefined}
                    label={w.name}
                    onClick={(e) => { e.stopPropagation(); onConfigureConnections && onConfigureConnections(flow); }}
                    style={{
                      cursor: "pointer",
                      fontSize: "0.65rem",
                      background: "rgba(37,211,102,0.15)",
                      color: "#25d366",
                      border: "1px solid rgba(37,211,102,0.3)",
                    }}
                  />
                )); })()
                : <Chip
                    size="small"
                    icon={<WifiTethering style={{ fontSize: 14 }} />}
                    label="Todas as conexoes"
                    onClick={(e) => { e.stopPropagation(); onConfigureConnections && onConfigureConnections(flow); }}
                    style={{
                      cursor: "pointer",
                      fontSize: "0.65rem",
                      background: "rgba(0,0,0,0.06)",
                      color: "inherit",
                      border: "1px solid rgba(0,0,0,0.1)",
                    }}
                  />
              }
            </Box>
          </Box>
        </Stack>
        
        <Menu
          className={classes.menu}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={(event) => {
            event.stopPropagation();
            handleAction(() => onExport(flow.id, flow.name));
          }}>
            <ContentCopy fontSize="small" style={{ marginRight: 12, color: theme.palette.text.secondary }} />
            Exportar (.zip)
          </MenuItem>
          <Divider />
          <MenuItem onClick={(event) => {
            event.stopPropagation();
            handleAction(() => onNavigate(flow.id));
          }}>
            <Build fontSize="small" style={{ marginRight: 12, color: theme.palette.text.secondary }} />
            Abrir fluxo
          </MenuItem>
          <Divider />
          <MenuItem onClick={(event) => {
            event.stopPropagation();
            handleAction(() => onEdit());
          }}>
            <Edit fontSize="small" style={{ marginRight: 12, color: theme.palette.text.secondary }} />
            Editar fluxo
          </MenuItem>
          <Divider />
          <MenuItem onClick={(event) => {
            event.stopPropagation();
            handleAction(() => onTrigger && onTrigger());
          }}>
            <span style={{ marginRight: 12, fontSize: 16 }}>⚡</span>
            Configurar Gatilho
          </MenuItem>
          <Divider />
          <MenuItem onClick={(event) => {
            event.stopPropagation();
            handleAction(() => onDelete());
          }}>
            <Delete fontSize="small" style={{ marginRight: 12, color: theme.palette.error.main }} />
            Excluir fluxo
          </MenuItem>
        </Menu>
      </CardContent>
    </Card>
  );
}

const FlowBuilder = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);
  const [importInputRef, setImportInputRef] = useState(null);
  const [connectionsModalOpen, setConnectionsModalOpen] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [connectionsFlow, setConnectionsFlow] = useState(null);
  const [selectedConnections, setSelectedConnections] = useState([]);
  const [availableWhatsapps, setAvailableWhatsapps] = useState([]);
  const [allWhatsapps, setAllWhatsapps] = useState([]);
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [triggerFlowId, setTriggerFlowId] = useState(null);
  const [triggerInput, setTriggerInput] = useState("");
  const [triggerIsCatchAll, setTriggerIsCatchAll] = useState(false);

  const handleOpenTrigger = (flow) => {
    setTriggerFlowId(flow.id);
    setTriggerInput((flow.triggers || []).join("\n"));
    setTriggerIsCatchAll(!!flow.isCatchAll);
    setTriggerDialogOpen(true);
  };

  const handleSaveTriggers = async () => {
    try {
      const phrases = triggerInput
        .split("\n")
        .map(t => t.trim())
        .filter(t => t.length > 0);
      await api.put(`/flowbuilder/${triggerFlowId}/triggers`, {
        triggers: phrases,
        isCatchAll: triggerIsCatchAll,
      });
      setTriggerDialogOpen(false);
      setReloadData(old => !old);
      toast.success("Gatilho salvo com sucesso!");
    } catch (err) {
      toastError(err);
    }
  };

  const [hasMore, setHasMore] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    api.get("/whatsapp/").then(({ data }) => {
      setAllWhatsapps(Array.isArray(data) ? data : (data?.whatsapps || []));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/flowbuilder");
          setWebhooks(data.flows);
          dispatch({ type: "LOAD_CONTACTS", payload: data.flows });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, reloadData]);

  useEffect(() => {
    const companyId = user.companyId;

    const onContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    };

    socket.on(`company-${companyId}-contact`, onContact);

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleEditContact = (contact) => {
    setSelectedContactId(contact.id);
    setSelectedWebhookName(contact.name);
    setContactModalOpen(true);
  };

  const handleToggleFlowActive = async (flowId, currentActive) => {
    try {
      const newActive = !currentActive;
      const { data } = await api.put(`/flowbuilder/${flowId}/toggle-active`, {
        active: newActive
      });
      
      // Atualizar o fluxo no estado
      dispatch({
        type: "UPDATE_CONTACTS",
        payload: { ...data.data, id: flowId }
      });
      
      // Atualizar também no estado webhooks
      setWebhooks((prev) =>
        prev.map((flow) =>
          flow.id === flowId ? { ...flow, active: newActive } : flow
        )
      );
      
      toast.success(data.message || `Fluxo ${newActive ? "ativado" : "desativado"} com sucesso`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenConnectionsModal = async (flow) => {
    try {
      const { data } = await api.get("/whatsapp/");
      setAvailableWhatsapps(data);
    } catch (e) {}
    setConnectionsFlow(flow);
    setSelectedConnections(flow.whatsappIds || []);
    setConnectionsModalOpen(true);
  };

  const handleSaveConnections = async () => {
    if (!connectionsFlow) return;
    try {
      await api.put("/flowbuilder/" + connectionsFlow.id + "/connections", { whatsappIds: selectedConnections });
      dispatch({ type: "UPDATE_CONTACTS", payload: { ...connectionsFlow, whatsappIds: selectedConnections } });
      setWebhooks(prev => prev.map(f => f.id === connectionsFlow.id ? { ...f, whatsappIds: selectedConnections } : f));
      toast.success("Conexoes salvas!");
    } catch (e) {
      toast.error("Erro ao salvar conexoes");
    }
    setConnectionsModalOpen(false);
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await api.delete(`/flowbuilder/${webhookId}`);
      setDeletingContact(null);
      setConfirmOpen(false);
      setReloadData((old) => !old);
      toast.success("Fluxo excluído com sucesso");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicateFlow = async (flowId) => {
    try {
      await api.post(`/flowbuilder/duplicate`, { flowId: flowId });
      setDeletingContact(null);
      setConfirmDuplicateOpen(false);
      setReloadData((old) => !old);
      toast.success("Fluxo duplicado com sucesso");
    } catch (err) {
      toastError(err);
    }
  };

  const handleExportFlow = async (flowId, name) => {
    try {
      const response = await api.get(`/flowbuilder/${flowId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name.replace(/[^a-z0-9-_]/gi, '_')}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const handleImportClick = () => {
    if (importInputRef) {
      importInputRef.click();
    }
  };

  const handleImportChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Por favor, selecione um arquivo ZIP (.zip)');
      e.target.value = null;
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post('/flowbuilder/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Fluxo importado com sucesso');
      setReloadData((old) => !old);
    } catch (err) {
      toastError(err);
    } finally {
      e.target.value = null;
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleReorder = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    const items = Array.from(webhooks);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    setWebhooks(items);
    try {
      await api.put("/flowbuilder/reorder", { orderedIds: items.map(f => f.id) });
    } catch (err) {
      toast.error("Erro ao salvar ordem dos fluxos");
    }
  };

  const filteredWebhooks = webhooks.filter(webhook =>
    webhook.name.toLowerCase().includes(searchParam.toLowerCase())
  );
  const activeFlowsCount = webhooks.filter(flow => flow.active).length;
  const inactiveFlowsCount = webhooks.length - activeFlowsCount;
  const connectedFlowsCount = webhooks.filter(flow => flow.whatsappIds && flow.whatsappIds.length > 0).length;

  if (user.showFlow !== "enabled" && user.profile !== "admin") {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer className={classes.mainContainer}>
      {/* Modais - mantendo todos originais */}
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      
      <FlowBuilderModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        flowId={selectedContactId}
        nameWebhook={selectedWebhookName}
        onSave={() => setReloadData((old) => !old)}
      />
      
      <ConfirmationModal
        title={
          deletingContact
            ? `Excluir fluxo "${deletingContact.name}"?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => {
          if (deletingContact) {
            handleDeleteWebhook(deletingContact.id);
          }
        }}
      >
        {deletingContact
          ? `Esta ação não pode ser desfeita. Todas as integrações relacionadas serão perdidas.`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>
      
      <ConfirmationModal
        title={
          deletingContact
            ? `Duplicar fluxo "${deletingContact.name}"?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmDuplicateOpen}
        onClose={setConfirmDuplicateOpen}
        onConfirm={() => {
          if (deletingContact) {
            handleDuplicateFlow(deletingContact.id);
          }
        }}
      >
        {deletingContact
          ? `Uma cópia do fluxo será criada para você editar.`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>

      {/* Trigger Dialog */}
      <Dialog open={triggerDialogOpen} onClose={() => setTriggerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontWeight: 700 }}>
          ⚡ Configurar Gatilho do Fluxo
        </DialogTitle>
        <DialogContent>
          <Box style={{ background: "rgba(99,102,241,0.06)", borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
            <Typography style={{ fontSize: "0.82rem", color: "#4338CA" }}>
              <strong>Como funciona:</strong> defina as frases que ativam este fluxo.
              Uma por linha. Pode ser palavra ou frase parcial (ex: "oi", "quero saber").
              Se nenhum outro fluxo for ativado, o <strong>Fluxo Padrão</strong> entra automaticamente.
            </Typography>
          </Box>

          <TextField
            label="Frases que ativam este fluxo (uma por linha)"
            multiline
            rows={5}
            fullWidth
            variant="outlined"
            value={triggerInput}
            onChange={e => setTriggerInput(e.target.value)}
            placeholder={"oi\nquero saber mais\npreço\nagendar"}
            style={{ marginBottom: 16 }}
            helperText="Deixe vazio se este for o Fluxo Padrão"
          />

          <FormControlLabel
            control={
              <Switch
                checked={triggerIsCatchAll}
                onChange={e => {
                  setTriggerIsCatchAll(e.target.checked);
                  if (e.target.checked) setTriggerInput("");
                }}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography style={{ fontWeight: 600, fontSize: "0.9rem" }}>Fluxo Padrão</Typography>
                <Typography style={{ fontSize: "0.75rem", opacity: 0.6 }}>
                  Ativa quando nenhuma frase de outros fluxos for reconhecida
                </Typography>
              </Box>
            }
          />
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setTriggerDialogOpen(false)} color="secondary">Cancelar</Button>
          <Button onClick={handleSaveTriggers} variant="contained" color="primary">Salvar Gatilho</Button>
        </DialogActions>
      </Dialog>

      {/* Connections Modal */}
      <Dialog open={connectionsModalOpen} onClose={() => setConnectionsModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Conexoes ativas para: {connectionsFlow && connectionsFlow.name}</DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: 16, color: "rgba(255,255,255,0.6)", fontSize: 13 }}>
            Selecione em quais conexoes este fluxo sera ativado. Se nenhuma for selecionada, o fluxo funciona em todas.
          </p>
          {availableWhatsapps.map(w => (
            <FormControlLabel
              key={w.id}
              control={
                <Checkbox
                  checked={selectedConnections.includes(w.id)}
                  onChange={e => {
                    if (e.target.checked) setSelectedConnections(prev => [...prev, w.id]);
                    else setSelectedConnections(prev => prev.filter(id => id !== w.id));
                  }}
                  color="primary"
                />
              }
              label={w.name + (w.status === "CONNECTED" ? " (conectada)" : " (desconectada)")}
              style={{ display: "flex", marginBottom: 4 }}
            />
          ))}
          {availableWhatsapps.length === 0 && (
            <p style={{ color: "rgba(255,255,255,0.4)" }}>Nenhuma conexao encontrada</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectionsModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveConnections} color="primary" variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Header seguindo padrão da página Tags */}
      <MainHeader>
        <Title>Fluxos de Conversa ({webhooks.length})</Title>
        <MainHeaderButtonsWrapper>
          <input
            type="file"
            accept=".zip,application/zip"
            ref={(ref) => setImportInputRef(ref)}
            style={{ display: 'none' }}
            onChange={handleImportChange}
          />
          <TextField
            className={classes.searchField}
            placeholder="Buscar fluxos..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#008C4A" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            onClick={handleImportClick}
            style={{ marginRight: 8, borderRadius: 10, textTransform: "none", fontWeight: 750, borderColor: "#B9CEC1", color: "#24372C" }}
          >
            Importar (.zip)
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenContactModal}
            style={{ borderRadius: 10, textTransform: "none", fontWeight: 850, background: "#00DB75", color: "#04130C", boxShadow: "0 7px 18px rgba(0,219,117,.22)" }}
          >
            Novo Fluxo
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Box className={classes.premiumHero}>
        <div className={classes.premiumHeroGlow} />
        <Box style={{ position: "relative", zIndex: 1 }}>
          <Typography className={classes.heroEyebrow}>Automação Estratiza Hub</Typography>
          <Typography className={classes.heroTitle}>Central de Fluxos de Conversa</Typography>
          <Typography className={classes.heroSubtitle}>
            Organize toda a jornada de atendimento do seu negócio.
          </Typography>
          <div className={classes.statsRow}>
            <div className={classes.statItem}>
              <span className={classes.statValue}>{webhooks.length}</span>
              <span className={classes.statLabel}>Fluxos totais</span>
            </div>
            <div className={classes.statItem}>
              <span className={classes.statValue}>{activeFlowsCount}</span>
              <span className={classes.statLabel}>Ativos</span>
            </div>
            <div className={classes.statItem}>
              <span className={classes.statValue}>{inactiveFlowsCount}</span>
              <span className={classes.statLabel}>Inativos</span>
            </div>
            <div className={classes.statItem}>
              <span className={classes.statValue}>{connectedFlowsCount}</span>
              <span className={classes.statLabel}>Com conexão específica</span>
            </div>
          </div>
        </Box>
      </Box>

      {/* Info banner */}
      <Box style={{
        background: "linear-gradient(135deg, rgba(0,219,117,.08) 0%, rgba(5,8,6,.02) 100%)",
        border: "1px solid rgba(0,168,90,.20)",
        borderRadius: 15,
        padding: "14px 17px",
        marginBottom: 16,
      }}>
        {/* Titulo sempre visivel */}
        <Typography style={{ fontSize: "0.82rem", fontWeight: 800, color: "#007A42", marginBottom: 4 }}>
          ⚡ Fluxos e Gatilhos: Regras de Prioridade
        </Typography>

        {/* Quando recolhido: mostra pill clicavel abaixo do titulo */}
        {!infoExpanded && (
          <Box
            onClick={() => setInfoExpanded(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              cursor: "pointer", marginTop: 2,
              background: "rgba(0,219,117,.10)",
              border: "1px solid rgba(0,168,90,.25)",
              borderRadius: 20, padding: "2px 10px",
            }}>
            <Typography style={{ fontSize: "0.72rem", color: "#007A42", fontWeight: 800, letterSpacing: "0.3px" }}>
              ▼ Leia tudo — como funciona · Clique aqui
            </Typography>
          </Box>
        )}

        {/* Conteudo expandido */}
        {infoExpanded && (
          <Typography style={{ fontSize: "0.78rem", color: "#555", lineHeight: 1.85 }}>

            O sistema decide qual fluxo executar seguindo esta ordem — do mais forte ao mais fraco:<br/><br/>

            <strong>1️⃣ Palavra-chave + Horário configurado</strong><br/>
            Prioridade máxima. Você configurou a frase <strong>"quero saber mais"</strong> como gatilho deste fluxo e definiu que ele só roda das 08h às 18h — quando o cliente enviar essa frase dentro desse horário, este fluxo é ativado e os demais são ignorados. Fora do horário configurado, mesmo que a frase seja enviada, este fluxo é pulado e o sistema busca o próximo na ordem.<br/><br/>

            <strong>2️⃣ Palavra-chave (sem horário)</strong><br/>
            Você configurou <strong>"falar com atendente"</strong> como gatilho e não definiu nenhum horário — sempre que o cliente enviar essa frase, o fluxo é ativado a qualquer hora do dia ou da noite, independente de qualquer outra configuração.<br/><br/>

            <strong>3️⃣ Fluxo Padrão com Horário</strong><br/>
            Nenhuma palavra-chave foi reconhecida. O sistema então busca um Fluxo Padrão que esteja ativo naquele momento. Você pode ter um Padrão marcado para 08h–18h (horário comercial) e outro para 18h–08h (fora do horário) — ambos marcados como Padrão, cada um com sua janela. O sistema troca automaticamente conforme o relógio, sem precisar fazer nada.<br/><br/>

            <strong>4️⃣ Fluxo Padrão sem horário — a rede de segurança</strong><br/>
            Se nenhuma das opções acima bater, este fluxo entra. Ele atende tudo que não foi capturado por nenhuma palavra-chave nem por nenhum Padrão com horário. Sempre tenha ao menos um Fluxo Padrão sem horário para garantir que nenhuma mensagem fique sem resposta.<br/><br/>

            <strong>📱 Conexões e Canais</strong><br/>
            Por padrão, todos os fluxos rodam em todas as conexões (WhatsApp, Instagram e Facebook). Use o botão de conexões em cada fluxo para restringir a canais específicos — por exemplo, um fluxo só para o WhatsApp Comercial e outro só para o Instagram.<br/><br/>

            <strong>⚠️ Limitação do Instagram e Facebook (regra da Meta, não da plataforma)</strong><br/>
            No WhatsApp funcionam todos os recursos: texto, botões, listas, imagens, vídeos e arquivos. No Instagram e Facebook a API da Meta aceita somente <strong>texto e Agente de IA</strong> — botões, listas, áudios e arquivos não são entregues nesses canais, é uma restrição da própria Meta sem possibilidade de contorno. Crie um fluxo com IA e textos para todos os canais, ou fluxos separados por conexão.<br/><br/>

            <span
              onClick={() => setInfoExpanded(false)}
              style={{ cursor: "pointer", color: "#007A42", fontWeight: 800, fontSize: "0.72rem" }}>
              ▲ Recolher
            </span>

          </Typography>
        )}
      </Box>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {loading && !webhooks.length ? (
          <div className={classes.loadingContainer}>
            <CircularProgress style={{ color: theme.palette.primary.main }} />
          </div>
        ) : filteredWebhooks.length === 0 ? (
          <div className={classes.emptyState}>
            <DevicesFold className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              {searchParam ? 'Nenhum fluxo encontrado' : 'Nenhum fluxo criado ainda'}
            </Typography>
            <Typography className={classes.emptyDescription}>
              {searchParam 
                ? 'Tente usar outros termos de pesquisa'
                : 'Crie seu primeiro fluxo de conversa para automatizar atendimentos'
              }
            </Typography>
            
            {!searchParam && (
              <Button
                className={classes.addButton}
                onClick={handleOpenContactModal}
                startIcon={<AddCircle />}
                style={{ marginTop: 24 }}
              >
                Criar Primeiro Fluxo
              </Button>
            )}
          </div>
        ) : (
          <DragDropContext onDragEnd={handleReorder}>
            <Droppable droppableId="flows-list" isDropDisabled={!!searchParam}>
              {(provided) => (
                <Stack spacing={2} {...provided.droppableProps} ref={provided.innerRef}>
                  {filteredWebhooks.map((flow, index) => (
                    <Draggable key={String(flow.id)} draggableId={String(flow.id)} index={index} isDragDisabled={!!searchParam}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{ ...provided.draggableProps.style, position: "relative", opacity: snapshot.isDragging ? 0.85 : 1 }}
                        >
                          {!searchParam && (
                            <div
                              {...provided.dragHandleProps}
                              title="Arrastar para reordenar"
                              style={{ position: "absolute", top: 10, left: 10, zIndex: 10, cursor: "grab", padding: 4, borderRadius: 4, display: "flex", alignItems: "center", color: "rgba(128,128,128,0.5)" }}
                            >
                              <DragIndicator style={{ fontSize: 18 }} />
                            </div>
                          )}
                          <FlowCard
                            flow={flow}
                            classes={classes}
                            onEdit={() => handleEditContact(flow)}
                            onDuplicate={() => {
                              setDeletingContact(flow);
                              setConfirmDuplicateOpen(true);
                            }}
                            onDelete={() => {
                              setDeletingContact(flow);
                              setConfirmOpen(true);
                            }}
                            onNavigate={(id) => history.push(`/flowbuilder/${id}`)}
                            onToggleActive={(id, active) => handleToggleFlowActive(id, active)}
                            onExport={handleExportFlow}
                            onConfigureConnections={handleOpenConnectionsModal}
                            onTrigger={() => handleOpenTrigger(flow)}
                            allWhatsapps={allWhatsapps}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {loading && webhooks.length > 0 && (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress size={24} style={{ color: theme.palette.primary.main }} />
                    </Box>
                  )}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Paper>

      {isMobile && (
        <HideOnScroll>
          <Fab
            className={classes.fab}
            onClick={handleOpenContactModal}
            aria-label="Criar fluxo"
          >
            <AddCircle />
          </Fab>
        </HideOnScroll>
      )}
    </MainContainer>
  );
};

export default FlowBuilder;
